/**
 * The small shared pieces. Deliberately hand-built on Radix primitives rather than
 * adopted from a component kit: the whole design argument is that this app should not
 * look like everything else, and shadcn's defaults would put it back.
 */
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { clsx } from "@/lib/clsx.ts";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

export function Button({
  variant = "default",
  size = "default",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "ghost";
  size?: "default" | "sm" | "icon";
}) {
  return (
    <button
      type="button"
      className={clsx(
        "btn",
        variant === "primary" && "btn-primary",
        variant === "ghost" && "btn-ghost",
        size === "sm" && "btn-sm",
        size === "icon" && "btn-icon",
        className,
      )}
      {...props}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx("input", className)} {...props} />;
}

export function Pill({
  tone = "bare",
  children,
}: {
  tone?: "green" | "amber" | "red" | "slate" | "accent" | "bare";
  children: ReactNode;
}) {
  return <span className={clsx("pill", `pill-${tone}`)}>{children}</span>;
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="eyebrow">{children}</div>;
}

/**
 * A tri-state checkbox with a real accessible name, role and `aria-checked`.
 *
 * The mockups drew these as bare `<span>`s: about a dozen focusable controls on the
 * workspace had no name at all, and document selection is the core mechanic of the
 * product. Radix gives keyboard handling and `aria-checked="mixed"` for free.
 */
export function TriCheckbox({
  state,
  onToggle,
  label,
  disabled,
}: {
  state: "on" | "off" | "partial";
  onToggle: () => void;
  /** Required. A checkbox with no name is invisible to a screen reader. */
  label: string;
  disabled?: boolean;
}) {
  return (
    <CheckboxPrimitive.Root
      checked={state === "partial" ? "indeterminate" : state === "on"}
      onCheckedChange={onToggle}
      disabled={disabled}
      aria-label={label}
      className={clsx("check", state === "on" && "checked", state === "partial" && "partial")}
    >
      <CheckboxPrimitive.Indicator>
        {state === "on" ? <TickIcon /> : null}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export function TickIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <path
        d="M1.5 5.2 3.8 7.5 8.5 2.5"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Panel({
  tone = "accent",
  title,
  children,
}: {
  tone?: "red" | "amber" | "slate" | "accent";
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={clsx("panel", `panel-${tone}`)} role="note">
      <div className="panel-title">{title}</div>
      {children ? <div className="panel-body">{children}</div> : null}
    </div>
  );
}

/**
 * Empty states. `.evid-empty` existed in the mockup CSS and was used on no page, so
 * every list rendered nothing at all when it had nothing — which reads as broken
 * rather than empty.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="evid-empty" style={{ padding: "var(--s7) var(--s5)" }}>
      <p style={{ fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>{title}</p>
      <p>{body}</p>
      {action ? <div style={{ marginTop: "var(--s4)" }}>{action}</div> : null}
    </div>
  );
}
